import type { Session } from '../auth/useSession'
import type { TopBarNav } from './TopBar'

export type FleetId = 'ultratech' | 'mbmt' | 'eim' | 'star' | 'global' | 'nagpur' | 'wondercement' | 'tupl'
export type SurfaceId = 'command' | 'reports' | 'live' | 'charging'

/** Every site open to any signed-in user -- MBMT, EIM/Ports, STAR Cement, Global and
 *  Nagpur used to be gated behind isSuperAdmin here (and server-side,
 *  _require_super_admin_raw_data in serving/app.py/mbmt.py); that restriction from when
 *  each site was added "ahead of merging this work to production" has been lifted.
 *  Parameter kept (unused) rather than removed so every call site (there are ~10) does
 *  not need touching for what is now a constant list. */
export function fleetPills(_isSuperAdmin: boolean): TopBarNav[] {
  return [
    { id: 'ultratech', label: 'Ultratech' },
    { id: 'mbmt', label: 'MBMT' },
    { id: 'eim', label: 'Ports' },
    { id: 'star', label: 'STAR Cement' },
    { id: 'nagpur', label: 'Nagpur' },
    { id: 'wondercement', label: 'Wonder Cement' },
    { id: 'tupl', label: 'TUPL' },
    { id: 'global', label: 'Global' },
  ]
}

export function surfacePills(fleet: FleetId): TopBarNav[] {
  if (fleet === 'mbmt' || fleet === 'tupl') {
    return [
      { id: 'live', label: 'Live' },
      { id: 'reports', label: 'Reports' },
    ]
  }
  if (fleet === 'eim' || fleet === 'star') {
    return [
      { id: 'live', label: 'Live' },
      { id: 'reports', label: 'Reports' },
    ]
  }
  // Global and Nagpur are each one page, not a Live/Reports pair -- no secondary nav.
  // Wonder Cement is the same for now: live map + roster only, first pass (mirrors
  // STAR's own phase-1 scope) -- a Reports surface is a fast follow, not this pass.
  if (fleet === 'global' || fleet === 'nagpur' || fleet === 'wondercement') return []
  return [
    { id: 'command', label: 'Command' },
    { id: 'reports', label: 'Reports' },
  ]
}

export function pathFor(fleet: FleetId, surface: SurfaceId): string {
  if (fleet === 'mbmt') {
    if (surface === 'reports') return '/mbmt/reports'
    if (surface === 'charging') return '/mbmt/charging'
    return '/bus-ops'
  }
  if (fleet === 'eim') {
    return surface === 'reports' ? '/eim/reports' : '/eim'
  }
  if (fleet === 'star') {
    return surface === 'reports' ? '/star/reports' : '/star'
  }
  if (fleet === 'global') return '/global'
  if (fleet === 'nagpur') return '/bus-ops-nagpur'
  if (fleet === 'wondercement') return '/wondercement'
  if (fleet === 'tupl') {
    if (surface === 'reports') return '/tupl/reports'
    return '/bus-ops-tupl'
  }
  return surface === 'reports' ? '/reports' : '/fleet'
}

/** Which surface a bare fleet switch (the TopBar's fleet pill, not the surface pill)
 *  should land on. Every shell's onFleet handler funnels through this rather than
 *  repeating its own two-way ternary -- the bug that motivated this: adding 'eim' to
 *  FleetId did nothing for the five shells that each hardcoded
 *  `id === 'mbmt' ? ... : ultratech`, so clicking the new EIM pill from anywhere except
 *  EimShell itself silently fell through to Ultratech Command. One function, so a future
 *  fourth fleet can't reproduce the same bug in six places. */
export function defaultSurfaceFor(fleet: FleetId): SurfaceId {
  // Meaningless for 'global'/'nagpur' (pathFor ignores the surface for both) -- 'live' is
  // just the least-wrong placeholder, same as it is for mbmt/eim/star.
  return fleet === 'mbmt' || fleet === 'eim' || fleet === 'star' || fleet === 'global'
      || fleet === 'nagpur' || fleet === 'wondercement' || fleet === 'tupl'
    ? 'live' : 'command'
}

/** Which `user_access.sites` codes open which console fleet. Ports (`eim`) is GTI + JM_BAXI;
 *  Global is the cross-site roll-up, so it is admin-only (null = no site code opens it). */
export const SITES_FOR_FLEET: Record<FleetId, readonly string[] | null> = {
  ultratech: ['ultratech'],
  mbmt: ['mbmt'],
  eim: ['gti', 'jm_baxi'],
  star: ['star_cement'],
  nagpur: ['nagpur'],
  wondercement: ['wondercement'],
  tupl: ['tupl'],
  global: null,
}

/**
 * The fleets this session may open. Courtesy only -- serving enforces the real rule
 * (auth.check_site) -- but it must agree with it, including the "permissive until
 * granted" case: an admin, or an account with no department assigned yet, sees every
 * fleet, exactly as the server treats them. A granted account sees only fleets whose
 * site it holds. An account granted nothing the console knows (e.g. management with no
 * sites) falls back to Ultratech rather than an empty, unreachable console.
 */
export function allowedFleetIds(session?: Session): FleetId[] {
  const all = Object.keys(SITES_FOR_FLEET) as FleetId[]
  if (!session || session.is_admin) return all
  // No department yet: open unless the server is set to fail closed (rbac_require_grant).
  if (!session.department && !session.rbac_require_grant) return all
  const held = new Set(session.sites ?? [])
  const ok = all.filter((f) => SITES_FOR_FLEET[f]?.some((site) => held.has(site)))
  return ok.length ? ok : ['ultratech']
}

export function visibleFleetPills(pills: TopBarNav[], session?: Session): TopBarNav[] {
  const ids = new Set<string>(allowedFleetIds(session))
  return pills.filter((p) => ids.has(p.id))
}

/** Where a session lands: its first permitted fleet, on that fleet's default surface. */
export function homePathFor(session?: Session): string {
  const first = allowedFleetIds(session)[0] ?? 'ultratech'
  return pathFor(first, defaultSurfaceFor(first))
}
